import * as yup from 'yup';
import { UserRole } from '../../../../enums/userEnums';
import { AccessDurationMode, UserFormStep } from '../../constants';
import type { CreateUserPayload, UpdateUserPayload, UserDetails, UserFormValues } from '../../../../types/UserManagementTypes';
import styles from './UserFormModal.module.scss';

export const SPECIAL_CHAR_REGEX = /[!@#$%^&*()\-_+=[\]{};:'",.<>?/\\|]/;

export const PHONE_MAX_DIGITS = 10;

export const FIELD_LABELS = {
  NAME: 'NAME',
  TITLE: 'TITLE',
  EMAIL: 'EMAIL',
  ACCESS: 'ACCESS',
  PHONE: 'PHONE',
  PASSWORD: 'PASSWORD',
  CONFIRM_PASSWORD: 'CONFIRM PASSWORD',
};

export const FIELD_PLACEHOLDERS = {
  NAME: 'Please enter first and last name.',
  TITLE: "Please assign the user's role or title.",
  EMAIL: "User's email address, which will be used as a login.",
  ACCESS: 'Please assign an access level.',
  PHONE: 'Please enter a contact phone number.',
  PASSWORD: 'Please enter a password.',
  CONFIRM_PASSWORD: 'Confirm password.',
};

export const STEP_HEADINGS = {
  USER: 'USER INFORMATION',
  GROUPS: 'ASSIGN GROUPS',
  SECURITY: 'SECURITY',
  SUMMARY: 'USER SUMMARY',
  ACCESS_DURATION: 'ACCESS DURATION',
};

export const GROUPS_STEP_NOTE = 'Assign the user to groups if desired. This can be changed in the Group Manager.';
export const SECURITY_STEP_NOTE = 'Please assign the user a password. They will be sent an email with their login information.';
export const REQUIRED_FIELD_NOTE = 'indicates a required field.';
export const ADD_GROUP_LABEL = '+ ADD NEW GROUP';
export const NEW_GROUP_PLACEHOLDER = 'Group name, then Enter';
export const EMPTY_VALUE = '—';

export const passwordRequirementsPopover = (
  <div className={styles.popoverContent}>
    <span>Your password must:</span>
    <ul className={styles.popoverList}>
      <li>Be at least 8 characters in length</li>
      <li>Include at least 1 uppercase letter</li>
      <li>Include at least 1 number</li>
      <li>Include one special character (i.e !, $, %, &amp; etc.)</li>
    </ul>
  </div>
);

export const accessDurationPopover = (
  <div className={styles.popoverContent}>
    <span>
      Please specify the duration that the user will be able to access their account. After the specified end date, the user will no longer be
      able to log in.
    </span>
  </div>
);

// CES Admin is ours, not a client's, so it is never assignable here.
export const ACCESS_ROLE_OPTIONS = Object.values(UserRole)
  .filter((role) => role !== UserRole.CES_ADMIN)
  .map((role) => ({ label: role, value: role }));

// Stable across every Sprint 7 Postman example (list/create/role-availability all agree);
// swap for GET /org/roles/availability if the backend starts assigning these dynamically.
const ROLE_ID_BY_NAME: Partial<Record<UserRole, number>> = {
  [UserRole.VIEWER]: 1,
  [UserRole.EDITOR]: 2,
  [UserRole.ADMIN]: 3,
};

export const ACCESS_DURATION_OPTIONS = [
  { label: 'Select Date', value: AccessDurationMode.SPECIFIC_DATE },
  { label: 'End of Subscription', value: AccessDurationMode.END_OF_SUBSCRIPTION },
];

export const USER_FORM_DEFAULTS: UserFormValues = {
  name: '',
  title: '',
  email: '',
  phone: '',
  role: null,
  groupIds: [],
  newGroupNames: [],
  password: '',
  confirmPassword: '',
  accessDurationMode: null,
  accessExpiryDate: null,
};

// Which fields Continue validates before it lets the step go.
export const USER_STEP_FIELDS: Record<UserFormStep, (keyof UserFormValues)[]> = {
  [UserFormStep.USER]: ['name', 'title', 'email', 'phone', 'role'],
  [UserFormStep.GROUPS]: [],
  [UserFormStep.SECURITY]: ['password', 'confirmPassword', 'accessDurationMode', 'accessExpiryDate'],
  [UserFormStep.SUMMARY]: [],
};

export const userFormValidation = yup.object({
  name: yup.string().trim().required('Name is required'),
  title: yup.string().trim(),
  email: yup.string().trim().required('Email is required').email('Enter a valid email address'),
  // Optional, but a partial number is worse than none.
  phone: yup
    .string()
    .trim()
    .matches(new RegExp(`^\\d{${PHONE_MAX_DIGITS}}$`), {
      message: `Enter a ${PHONE_MAX_DIGITS} digit phone number`,
      excludeEmptyString: true,
    }),
  role: yup.string().nullable().required('Access level is required'),
  groupIds: yup.array().of(yup.string()),
  newGroupNames: yup.array().of(yup.string()),
  // Editing: password is optional (blank = leave unchanged), but if the admin types one, it must meet
  // the same strength rules as creation.
  password: yup.string().when('$isEditing', {
    is: true,
    then: (schema) =>
      schema
        .notRequired()
        .test('min', 'Must be at least 8 characters', (value) => !value || value.length >= 8)
        .test('uppercase', 'Must include at least 1 uppercase letter', (value) => !value || /[A-Z]/.test(value))
        .test('number', 'Must include at least 1 number', (value) => !value || /[0-9]/.test(value))
        .test('special', 'Must include at least 1 special character', (value) => !value || SPECIAL_CHAR_REGEX.test(value)),
    otherwise: (schema) =>
      schema
        .required('Password is required')
        .min(8, 'Must be at least 8 characters')
        .matches(/[A-Z]/, 'Must include at least 1 uppercase letter')
        .matches(/[0-9]/, 'Must include at least 1 number')
        .matches(SPECIAL_CHAR_REGEX, 'Must include at least 1 special character'),
  }),
  confirmPassword: yup.string().when('$isEditing', {
    is: true,
    then: (schema) =>
      schema.notRequired().test('match', 'Passwords must match', function matchesPassword(value) {
        return !this.parent.password || value === this.parent.password;
      }),
    otherwise: (schema) => schema.required('Please confirm your password').oneOf([yup.ref('password')], 'Passwords must match'),
  }),
  accessDurationMode: yup.string().nullable().required('Access duration is required'),
  accessExpiryDate: yup
    .string()
    .nullable()
    .when('accessDurationMode', {
      is: AccessDurationMode.SPECIFIC_DATE,
      then: (schema) => schema.required('Please choose an expiry date'),
    }),
});

export const detailsToFormValues = (details: UserDetails): UserFormValues => ({
  name: details.name,
  title: details.title,
  email: details.email,
  phone: details.phone ?? '',
  role: details.role.name as UserRole,
  groupIds: details.groups.map((group) => String(group.id)),
  newGroupNames: [],
  password: '',
  confirmPassword: '',
  accessDurationMode: details.accessDurationType === 'SELECT_DATE' ? AccessDurationMode.SPECIFIC_DATE : AccessDurationMode.END_OF_SUBSCRIPTION,
  accessExpiryDate: details.accessExpiresAt,
});

const buildAccessDuration = (values: UserFormValues) =>
  values.accessDurationMode === AccessDurationMode.SPECIFIC_DATE
    ? ({ type: 'SELECT_DATE', expiresAt: values.accessExpiryDate ?? undefined } as const)
    : ({ type: 'END_OF_SUBSCRIPTION' } as const);

const buildUserFields = (values: UserFormValues) => ({
  name: values.name,
  title: values.title,
  email: values.email,
  phone: values.phone,
  roleId: ROLE_ID_BY_NAME[values.role as UserRole] as number,
});

// PUT acts like PATCH: omit unchanged keys instead of resending them.
export const formValuesToUpdatePayload = (
  values: UserFormValues,
  groupIds: number[],
  dirtyFields: Partial<Record<keyof UserFormValues, unknown>>,
): UpdateUserPayload => {
  const user: UpdateUserPayload['user'] = {};
  if (dirtyFields.name) user.name = values.name;
  if (dirtyFields.title) user.title = values.title;
  if (dirtyFields.email) user.email = values.email;
  if (dirtyFields.phone) user.phone = values.phone;
  if (dirtyFields.role) user.roleId = ROLE_ID_BY_NAME[values.role as UserRole] as number;

  const groupsChanged = !!(dirtyFields.groupIds || dirtyFields.newGroupNames);
  const passwordChanged = !!(dirtyFields.password && values.password);
  const securityChanged = !!(dirtyFields.accessDurationMode || dirtyFields.accessExpiryDate || passwordChanged);

  return {
    ...(Object.keys(user).length ? { user } : {}),
    ...(groupsChanged ? { groups: groupIds } : {}),
    ...(securityChanged
      ? { security: { ...(passwordChanged ? { password: values.password } : {}), accessDuration: buildAccessDuration(values) } }
      : {}),
  };
};

// groupIds is a caller-resolved id list rather than values.groupIds directly: Create's final list
// includes ids for newly-created groups, which don't exist anywhere in form state.
export const formValuesToCreatePayload = (values: UserFormValues, groupIds: number[]): CreateUserPayload => ({
  user: buildUserFields(values),
  groups: groupIds,
  security: { password: values.password, accessDuration: buildAccessDuration(values) },
});
